<template>
  <v-card flat color="transparent" class="pa-4">
    <v-row align="center" class="mb-2">
      <v-col cols="12" md="8">
        <div class="d-flex align-center" style="gap: 6px">
          <h2 class="font-weight-bold">Cola de producción</h2>
          <produccion-ayuda />
        </div>
        <p class="grey--text mb-0">Tomá el próximo corte en una máquina libre; finalizalo al terminar.</p>
      </v-col>
      <v-col cols="12" md="4" class="text-md-right">
        <v-btn outlined color="deep-purple" :to="{ name: 'empresa.produccion.plan' }">
          <v-icon left>mdi-clipboard-list</v-icon> Ver plan
        </v-btn>
      </v-col>
    </v-row>

    <div v-if="loading" class="text-center py-10">
      <v-progress-circular indeterminate color="deep-purple" />
    </div>

    <template v-else>
      <!-- MÁQUINAS -->
      <div class="overline mb-2 grey--text">
        <v-icon small>mdi-robot-industrial</v-icon> Máquinas
      </div>
      <v-row>
        <v-col v-for="m in maquinasActivas" :key="m.id" cols="12" md="6">
          <!-- OCUPADA -->
          <v-card v-if="ocupadaPor(m)" outlined style="border: 1px solid #FFA000">
            <v-card-title class="subtitle-1 font-weight-bold">
              {{ m.nombre }}
              <v-spacer />
              <v-chip small color="amber darken-2" dark>Cortando</v-chip>
            </v-card-title>
            <v-card-text>
              <div class="d-flex align-center">
                <div class="text-h3 font-weight-bold amber--text text--darken-2" style="font-variant-numeric: tabular-nums">
                  {{ elapsed(ocupadaPor(m)) }}
                </div>
                <div class="ml-4">
                  <div class="font-weight-bold body-1">{{ ocupadaPor(m).receta_nombre }}</div>
                  <div class="caption grey--text">
                    {{ cant(ocupadaPor(m).cantidad_lotes) }} placas
                    <span v-if="ocupadaPor(m).duracion_estimada_minutos"> · est. {{ tiempoHumano(ocupadaPor(m).duracion_estimada_minutos) }}</span>
                  </div>
                  <div v-if="ocupadaPor(m).pedidos_relacionados && ocupadaPor(m).pedidos_relacionados.length" class="caption mt-1">
                    <v-icon x-small color="blue-grey lighten-1">mdi-package-variant</v-icon>
                    <span class="grey--text mr-1">Cubre:</span>
                    <span v-for="(p, i) in ocupadaPor(m).pedidos_relacionados" :key="p.id">
                      <router-link :to="{ name: 'empresa.ventas.orden.consultar', params: { id: p.id } }" class="blue-grey--text text--darken-1">{{ p.cliente || p.numero_display }}</router-link><span v-if="i < ocupadaPor(m).pedidos_relacionados.length - 1">,&nbsp;</span>
                    </span>
                  </div>
                </div>
              </div>
              <div class="d-flex mt-3">
                <v-btn text small :to="{ name: 'empresa.produccion.orden.consultar', params: { id: ocupadaPor(m).id } }">Ver</v-btn>
                <v-spacer />
                <v-btn large color="success" :loading="actuando === ocupadaPor(m).id" :disabled="actuando !== null" @click="finalizar(ocupadaPor(m))">
                  <v-icon left>mdi-flag-checkered</v-icon> Finalizar
                </v-btn>
              </div>
            </v-card-text>
          </v-card>

          <!-- LIBRE -->
          <v-card v-else outlined class="fill-height">
            <v-card-text class="d-flex align-center py-4">
              <v-icon color="green" size="40" class="mr-4">mdi-check-circle-outline</v-icon>
              <div class="flex-grow-1">
                <div class="font-weight-bold body-1">
                  {{ m.nombre }} · <span class="green--text">Libre</span>
                </div>
                <div v-if="siguiente" class="caption grey--text">
                  Siguiente: <strong>{{ siguiente.receta_nombre }}</strong>
                  · {{ cant(siguiente.cantidad_lotes) }} placas
                  <span v-if="siguiente.duracion_estimada_minutos"> · {{ tiempoHumano(siguiente.duracion_estimada_minutos) }}</span>
                  <span v-if="siguiente.pedidos_relacionados && siguiente.pedidos_relacionados.length">
                    · <v-icon x-small>mdi-package-variant</v-icon>
                    <span v-for="(p, i) in siguiente.pedidos_relacionados" :key="p.id">{{ p.cliente || p.numero_display }}<span v-if="i < siguiente.pedidos_relacionados.length - 1">, </span></span>
                  </span>
                </div>
                <div v-else class="caption grey--text">Sin pendientes</div>
              </div>
              <v-btn
                v-if="siguiente"
                large color="deep-purple" dark
                :loading="actuando === siguiente.id"
                :disabled="actuando !== null"
                @click="iniciarEn(siguiente, m)"
              >
                <v-icon left>mdi-play</v-icon> Tomar
              </v-btn>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- POR CORTAR -->
      <div class="overline mb-2 mt-6 grey--text">Por cortar ({{ pendientes.length }})</div>

      <v-alert v-if="pendientes.length === 0" type="success" text dense>
        No hay órdenes pendientes. Generá desde el
        <router-link :to="{ name: 'empresa.produccion.plan' }">plan</router-link>.
      </v-alert>

      <v-row v-else>
        <v-col v-for="orden in pendientes" :key="orden.id" cols="12" md="6" lg="4">
          <v-card outlined class="fill-height d-flex flex-column">
            <v-card-title class="subtitle-1 font-weight-bold pb-1">
              {{ orden.receta_nombre }}
              <v-spacer />
              <span class="caption grey--text">{{ orden.numero_display }}</span>
            </v-card-title>
            <v-card-text class="flex-grow-1 pb-2">
              <div class="d-flex align-center">
                <v-avatar color="deep-purple" size="44" class="mr-3">
                  <span class="white--text text-h6 font-weight-bold">{{ cant(orden.cantidad_lotes) }}</span>
                </v-avatar>
                <div>
                  <div class="font-weight-bold">placas a cortar</div>
                  <div v-if="orden.duracion_estimada_minutos" class="caption grey--text">{{ tiempoHumano(orden.duracion_estimada_minutos) }}</div>
                </div>
              </div>
              <div v-if="orden.pedidos_relacionados && orden.pedidos_relacionados.length" class="mt-3">
                <div class="caption grey--text mb-1">
                  <v-icon x-small>mdi-package-variant</v-icon> Cubre demanda de:
                </div>
                <div v-for="p in orden.pedidos_relacionados" :key="p.id" class="d-flex align-center mb-1" style="gap: 6px">
                  <v-chip x-small :color="p.prioridad > 0 ? 'deep-purple' : 'grey lighten-2'" :dark="p.prioridad > 0" class="flex-shrink-0">
                    {{ p.prioridad > 0 ? '#' + p.prioridad : '—' }}
                  </v-chip>
                  <router-link :to="{ name: 'empresa.ventas.orden.consultar', params: { id: p.id } }" class="caption text-decoration-none">{{ p.cliente || p.numero_display }}</router-link>
                </div>
              </div>
            </v-card-text>
            <v-divider />
            <div v-if="puedeRepartir(orden)" class="px-3 pt-2">
              <v-btn
                small block text color="deep-purple"
                :loading="actuando === orden.id"
                :disabled="actuando !== null"
                @click="repartir(orden)"
              >
                <v-icon left small>mdi-call-split</v-icon>
                Repartir en {{ maquinasActivas.length }} máquinas
              </v-btn>
            </div>
            <v-card-actions class="pa-3 pt-1 flex-wrap">
              <span class="caption grey--text mr-1">Iniciar en:</span>
              <v-btn
                v-for="m in maquinasActivas"
                :key="m.id"
                color="deep-purple" dark class="ma-1"
                :disabled="!!ocupadaPor(m) || actuando !== null"
                :loading="actuando === orden.id"
                @click="iniciarEn(orden, m)"
              >
                <v-icon left small>mdi-play</v-icon> {{ nombreCorto(m) }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
    </template>
  </v-card>
</template>

<script>
import { mapActions } from 'vuex';
import ProduccionAyuda from '../ProduccionAyuda.vue';

export default {
  name: 'ColaProduccion',
  components: { ProduccionAyuda },
  data() {
    return { ordenes: [], maquinas: [], loading: false, actuando: null, ahora: Date.now(), tick: null, poll: null };
  },
  computed: {
    maquinasActivas() {
      return this.maquinas.filter(m => m.activa !== false);
    },
    pendientes() {
      return this.ordenes.filter(o => o.estado === 'borrador').slice().sort((a, b) => a.numero - b.numero);
    },
    siguiente() {
      return this.pendientes[0] || null;
    },
  },
  methods: {
    ...mapActions('ordenProduccion', ['fetch_ordenes', 'iniciar_orden', 'confirmar_orden', 'dividir_orden']),
    ...mapActions('maquinaProduccion', ['fetch_maquinas']),

    cant(v) {
      return Number(parseFloat(v)).toLocaleString('es-AR');
    },
    nombreCorto(m) {
      return m.nombre.replace(/^cortadora\s+/i, '');
    },
    tiempoHumano(min) {
      const t = Math.round(parseFloat(min) || 0);
      if (t < 60) return `~${t} min`;
      const h = Math.floor(t / 60);
      const mm = t % 60;
      return mm ? `~${h}h ${mm}m` : `~${h}h`;
    },
    snack(color, text, icon) {
      this.$updateBus.$emit('showSnackBar', { show: true, button: false, color, text, icon, timeout: 3500 });
    },

    ocupadaPor(maquina) {
      return this.ordenes.find(o => o.estado === 'en_proceso' && o.maquina === maquina.id) || null;
    },

    puedeRepartir(orden) {
      return this.maquinasActivas.length >= 2 && parseFloat(orden.cantidad_lotes) >= 2;
    },

    async repartir(orden) {
      this.actuando = orden.id;
      try {
        const res = await this.dividir_orden({ id: orden.id, partes: this.maquinasActivas.length });
        this.snack('info', `Repartida en ${res.data.cantidad} partes`, 'mdi-call-split');
        await this.fetchData();
      } catch (e) {
        const msg = e.response?.data?.error || 'No se pudo repartir';
        this.snack('error', msg, 'mdi-alert');
      } finally {
        this.actuando = null;
      }
    },

    elapsed(orden) {
      if (!orden || !orden.fecha_inicio) return '00:00';
      const s = Math.max(0, Math.floor((this.ahora - new Date(orden.fecha_inicio).getTime()) / 1000));
      const pad = n => String(n).padStart(2, '0');
      const hh = Math.floor(s / 3600);
      const mm = Math.floor((s % 3600) / 60);
      const ss = s % 60;
      return hh > 0 ? `${hh}:${pad(mm)}:${pad(ss)}` : `${pad(mm)}:${pad(ss)}`;
    },

    async fetchData(silent = false) {
      if (!silent) this.loading = true;
      try {
        const [o, m] = await Promise.all([this.fetch_ordenes(), this.fetch_maquinas()]);
        this.ordenes = o.data;
        this.maquinas = m.data;
      } catch (e) {
        if (!silent) console.error(e);
      } finally {
        if (!silent) this.loading = false;
      }
    },

    async iniciarEn(orden, maquina) {
      this.actuando = orden.id;
      try {
        await this.iniciar_orden({ id: orden.id, maquina: maquina.id });
        this.snack('info', `${orden.receta_nombre} en ${maquina.nombre} ⏱️`, 'mdi-play');
        await this.fetchData();
      } catch (e) {
        const msg = e.response?.data?.error || 'No se pudo iniciar';
        this.snack('error', msg, 'mdi-alert');
      } finally {
        this.actuando = null;
      }
    },

    async finalizar(orden) {
      this.actuando = orden.id;
      try {
        await this.confirmar_orden(orden.id);
        this.snack('success', `${orden.receta_nombre}: producido, stock actualizado`, 'mdi-check');
        await this.fetchData();
      } catch (e) {
        const msg = e.response?.data?.error || 'No se pudo finalizar (¿stock de insumos?)';
        this.snack('error', msg, 'mdi-alert');
      } finally {
        this.actuando = null;
      }
    },
  },
  created() {
    this.fetchData();
    this.tick = setInterval(() => { this.ahora = Date.now(); }, 1000);
    this.poll = setInterval(() => { if (this.actuando === null) this.fetchData(true); }, 8000);
  },
  beforeDestroy() {
    if (this.tick) clearInterval(this.tick);
    if (this.poll) clearInterval(this.poll);
  },
};
</script>
